import { startTransition, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { getConfig } from "lib/get-config";
import { getImgSrc } from "lib/get-img-src";
import { ContentNode } from "lib/types";

import ZoomInIcon from "assets/icons/zoom-in.svg?react";
import ZoomOutIcon from "assets/icons/zoom-out.svg?react";
import css from "./image.module.scss";

export type ImageProps = {
  node: ContentNode;
};

export function Image({ node, ...props }: ImageProps) {
  const src = node.s;
  const originalWidth = node.w || 0;
  const originalHeight = node.h || 0;
  const alt = "";
  const [isZoomed, setIsZoomed] = useState(false);
  const [imageRect, setImageRect] = useState<DOMRect | null>(null);
  const [canZoom, setCanZoom] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const zoomedImageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const img = imageRef.current;
    if (img) {
      function checkZoomable() {
        startTransition(() => {
          setCanZoom(
            img.naturalWidth > img.clientWidth ||
              img.naturalHeight > img.clientHeight
          );
        });
      }

      if (img.complete) {
        checkZoomable();
      } else {
        img.addEventListener("load", checkZoomable);
      }

      const resizeObserver = new ResizeObserver(checkZoomable);
      resizeObserver.observe(img);

      return () => {
        img.removeEventListener("load", checkZoomable);
        resizeObserver.disconnect();
      };
    }
  }, []);

  useEffect(() => {
    if (isZoomed && zoomedImageRef.current) {
      const rect = zoomedImageRef.current.getBoundingClientRect();
      setImageRect(rect);
    }
  }, [isZoomed]);

  const { contentUrl } = getConfig();
  const imageSrc = getImgSrc(contentUrl, src);

  // aspect-ratio для резервирования места
  const aspectRatio =
    originalWidth && originalHeight
      ? originalWidth / originalHeight
      : undefined;

  return (
    <>
      <span className={css.imageContainer}>
        <img
          ref={imageRef}
          {...props}
          src={imageSrc}
          alt={alt}
          className={css.image}
          loading="lazy"
          style={
            originalWidth && aspectRatio
              ? { width: originalWidth, aspectRatio }
              : undefined
          }
        />
        {canZoom && !isZoomed && (
          <button
            className={css.zoomInButton}
            onClick={() => {
              setIsZoomed(true);
            }}
          >
            <ZoomInIcon width={46} height={46} />
          </button>
        )}
      </span>

      {isZoomed &&
        createPortal(
          <div
            className={css.zoomedOverlay}
            onClick={() => {
              setIsZoomed(!isZoomed);
            }}
          >
            <img
              ref={zoomedImageRef}
              src={imageSrc}
              alt={alt}
              className={css.zoomedImage}
              onClick={e => e.stopPropagation()}
            />
            <button
              className={css.zoomOutButton}
              onClick={e => {
                e.stopPropagation();
                setIsZoomed(!isZoomed);
              }}
              style={{
                position: "absolute",
                left: imageRect ? `${imageRect.left + 8}px` : "50%",
                top: imageRect ? `${imageRect.top + 8}px` : "50%",
              }}
            >
              <ZoomOutIcon width={46} height={46} />
            </button>
          </div>,
          document.body
        )}
    </>
  );
}
